import { useState, useRef, useCallback, useEffect } from 'react';
import { setRecordingActive } from '../audio';

export interface UseVoiceRecorderReturn {
  isRecording: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  audioDataUrl: string | null;
  error: string | null;
  prepareMicrophone: () => Promise<MediaStream>;
  startRecording: (existingStream?: MediaStream) => Promise<void>;
  stopRecording: () => void;
  resetRecording: () => void;
  playPreview: () => void;
  isPlayingPreview: boolean;
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDataUrl, setAudioDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      setRecordingActive(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          // ignore
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
    };
  }, []);

  const resetRecording = useCallback(() => {
    setRecordingActive(false);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setAudioDataUrl(null);
    setError(null);
    setIsPlayingPreview(false);
    audioChunksRef.current = [];
  }, []);

  const prepareMicrophone = useCallback(async (): Promise<MediaStream> => {
    resetRecording();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("L'enregistrement vocal n'est pas supporté par ce navigateur.");
    }
    setRecordingActive(true);
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    streamRef.current = stream;
    return stream;
  }, [resetRecording]);

  const startRecording = useCallback(async (existingStream?: MediaStream) => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("L'enregistrement vocal n'est pas supporté par ce navigateur.");
      }

      setRecordingActive(true);

      let stream = existingStream || streamRef.current;
      if (!stream || stream.getTracks().length === 0 || stream.getTracks().every(t => t.readyState === 'ended')) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        streamRef.current = stream;
      }

      // Choose supported MIME type safely
      let options: MediaRecorderOptions = {};
      if (typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          options = { mimeType: 'audio/webm;codecs=opus' };
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          options = { mimeType: 'audio/webm' };
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          options = { mimeType: 'audio/mp4' };
        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
          options = { mimeType: 'audio/aac' };
        }
      }

      let mediaRecorder: MediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, options);
      } catch {
        mediaRecorder = new MediaRecorder(stream);
      }

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const actualType = mediaRecorder.mimeType || options.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualType });
        setAudioBlob(blob);

        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            setAudioDataUrl(reader.result);
          }
        };
        reader.readAsDataURL(blob);

        // Stop mic tracks to free the microphone
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        // Re-enable audio environment once recording is finished
        setRecordingActive(false);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);

      const startTime = Date.now();
      timerIntervalRef.current = window.setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTime) / 1000));
      }, 200);
    } catch (err: any) {
      console.error('Microphone error:', err);
      setRecordingActive(false);
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      setError(
        isDenied
          ? "Accès au micro refusé. Veuillez autoriser le microphone dans votre navigateur."
          : "Erreur lors de l'accès au microphone."
      );
      setIsRecording(false);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.warn('Error stopping recorder:', err);
      }
    }
    setIsRecording(false);
    setRecordingActive(false);
  }, []);

  const playPreview = useCallback(() => {
    if (!audioDataUrl) return;
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }

    const audio = new Audio(audioDataUrl);
    previewAudioRef.current = audio;
    setIsPlayingPreview(true);

    audio.onended = () => {
      setIsPlayingPreview(false);
      previewAudioRef.current = null;
    };
    audio.onerror = () => {
      setIsPlayingPreview(false);
      previewAudioRef.current = null;
    };

    audio.play().catch((e) => {
      console.warn('Preview play error:', e);
      setIsPlayingPreview(false);
    });
  }, [audioDataUrl]);

  return {
    isRecording,
    recordingTime,
    audioBlob,
    audioDataUrl,
    error,
    prepareMicrophone,
    startRecording,
    stopRecording,
    resetRecording,
    playPreview,
    isPlayingPreview,
  };
}
